import { useCallback, useEffect, useRef, useState } from "react";
import { authService, AuthRequestError } from "../../services/auth";
import { emitLoginEvent } from "./loginPolicy";

type QrState = {
  url: string | null;
  loading: boolean;
  expired: boolean;
  error: string | null;
};
interface WeChatLoginOptions {
  /** 只有选中微信、已同意协议且平台开启时才启动二维码。 */
  enabled: boolean;
  onAuthenticated: () => void;
  onBindPhone: (token: string) => void;
}

/** 管理单次扫码生命周期；切换、撤销协议、隐藏或卸载都会取消尚未完成的请求。 */
export function useWeChatLogin(options: WeChatLoginOptions) {
  const callbacks = useRef(options);
  callbacks.current = options;
  const [generation, setGeneration] = useState(0);
  const [state, setState] = useState<QrState>({
    url: null,
    loading: false,
    expired: false,
    error: null,
  });
  const refresh = useCallback(() => setGeneration((value) => value + 1), []);

  useEffect(() => {
    if (!options.enabled) {
      setState({ url: null, loading: false, expired: false, error: null });
      return;
    }
    let disposed = false;
    let token: string | null = null;
    let expiresAt = 0;
    let request: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let requestTimeout: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    const cancelRequest = () => {
      request?.abort();
      request = null;
      clearTimeout(timer);
      clearTimeout(requestTimeout);
    };
    const expire = () => {
      token = null;
      cancelRequest();
      setState((previous) => ({ ...previous, loading: false, expired: true }));
      emitLoginEvent("wechat_qr_status", {
        status: "expired",
        elapsed_ms: Date.now() - startedAt,
      });
    };
    const poll = async () => {
      if (disposed || document.hidden || !token) return;
      if (Date.now() >= expiresAt) {
        expire();
        return;
      }
      const current = new AbortController();
      request = current;
      // 单次请求也有超时，不能让一个断开的连接耗尽整个二维码生命周期。
      const timeout = setTimeout(() => current.abort(), 15_000);
      requestTimeout = timeout;
      try {
        const result = await authService.checkWeChatStatus(
          token,
          current.signal,
        );
        if (disposed || current.signal.aborted) return;
        setState((previous) => ({ ...previous, error: null }));
        if (result.status !== "pending") {
          clearTimeout(expiryTimer);
          token = null;
          emitLoginEvent("wechat_qr_status", {
            status: result.status,
            elapsed_ms: Date.now() - startedAt,
          });
          if (result.status === "authenticated")
            callbacks.current.onAuthenticated();
          else callbacks.current.onBindPhone(result.wechatToken);
          return;
        }
        timer = setTimeout(() => void poll(), 2000);
      } catch (error) {
        if (disposed || document.hidden || request !== current || !token)
          return;
        const message = error instanceof Error ? error.message : "";
        if (/过期|expired/i.test(message)) {
          expire();
          return;
        }
        const terminal =
          error instanceof AuthRequestError &&
          error.status > 0 &&
          error.status < 500 &&
          error.status !== 429;
        setState((previous) => ({
          ...previous,
          error: current.signal.aborted ? null : message,
          expired: terminal,
        }));
        if (terminal) {
          token = null;
          clearTimeout(expiryTimer);
          return;
        }
        const delay =
          error instanceof AuthRequestError
            ? Math.max(5000, error.retryAfterSeconds * 1000)
            : 5000;
        timer = setTimeout(() => void poll(), delay);
      } finally {
        clearTimeout(timeout);
      }
    };
    const create = async () => {
      if (disposed || document.hidden) return;
      setState({ url: null, loading: true, expired: false, error: null });
      const current = new AbortController();
      request = current;
      const timeout = setTimeout(() => current.abort(), 15_000);
      requestTimeout = timeout;
      try {
        const session = await authService.createWeChatSession(current.signal);
        if (disposed || current.signal.aborted) return;
        token = session.sessionToken;
        expiresAt = Date.now() + session.expiresIn * 1000;
        setState({
          url: session.qrCodeUrl,
          loading: false,
          expired: false,
          error: null,
        });
        emitLoginEvent("wechat_qr_status", {
          status: "pending",
          elapsed_ms: Date.now() - startedAt,
        });
        expiryTimer = setTimeout(expire, session.expiresIn * 1000);
        timer = setTimeout(() => void poll(), 2000);
      } catch (error) {
        if (disposed || document.hidden || request !== current) return;
        setState({
          url: null,
          loading: false,
          expired: true,
          error:
            error instanceof Error && !current.signal.aborted
              ? error.message
              : null,
        });
      } finally {
        clearTimeout(timeout);
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) cancelRequest();
      else if (token) void poll();
      else if (!expiresAt) void create();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    void create();
    return () => {
      disposed = true;
      cancelRequest();
      clearTimeout(expiryTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [options.enabled, generation]);
  return { ...state, refresh };
}
